import type { Theme } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import type { StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';
export interface BrandThemes {
  readonly light: Theme;
  readonly dark: Theme;
}

export type TokensAndStylesType = BrandThemes | null;

export interface ActiveNetworkBranding {
  readonly id: string;
  readonly name: string;
  readonly colorScheme: string;
  readonly tokensAndStyles: TokensAndStylesType;
}

export type LogoTypes = 'SMALL' | 'LARGE';
export interface BrandThemeState {
  readonly organizationalThemeClientData: string | null | undefined;
  readonly brandName: string | null | undefined;
  readonly brandSmallLogoUrl: string | null | undefined;
  readonly brandLargeLogoUrl: string | null | undefined;
  readonly brandTheme: ActiveNetworkBranding | null | undefined;
  readonly error: string | null;
  readonly hasLoaded: boolean;
  /**
   * True when either color or name/logo preview is active.
   * For color-only checks, use the server field
   * `tenantConfiguration.viewerActiveNetworkBranding.viewerIsPreviewing` instead.
   */
  readonly viewerIsPreviewing: boolean;
  readonly isPreviewingNameOrLogo: boolean;

  readonly setOrganizationalThemeClientData: (organizationalThemeClientData: string | null | undefined) => void;
  setBrandName(brandName: string | null | undefined): void;
  setBrandLogoUrl(logoType: LogoTypes, url: string | null | undefined): void;
  setBrandTheme(brandTheme: ActiveNetworkBranding | null | undefined): void;
  setError(error: string | null | undefined): void;
  setViewerIsPreviewing(viewerIsPreviewing: boolean): void;
  setIsPreviewingNameOrLogo(isPreviewingNameOrLogo: boolean): void;
  resetState(): void;
}

export const useBrandThemeStore: UseBoundStore<StoreApi<BrandThemeState>> = create<BrandThemeState>((set) => ({
  organizationalThemeClientData: undefined,
  brandName: null,
  brandSmallLogoUrl: null,
  brandLargeLogoUrl: null,
  brandTheme: null,
  error: null,
  hasLoaded: false,
  viewerIsPreviewing: false,
  isPreviewingNameOrLogo: false,

  setOrganizationalThemeClientData: (organizationalThemeClientData) => {
    set({ organizationalThemeClientData });
  },
  setBrandName: (brandName) => {
    set({ brandName });
  },
  setBrandLogoUrl: (logoType: LogoTypes, url: string | null) => {
    set(logoType === 'SMALL' ? { brandSmallLogoUrl: url } : { brandLargeLogoUrl: url });
  },
  setBrandTheme: (brandTheme) => {
    set({
      brandTheme,
      error: null,
      hasLoaded: true,
    });
  },
  setError: (error) => {
    set({ error, hasLoaded: true });
  },
  setViewerIsPreviewing: (viewerIsPreviewing) => {
    set({ viewerIsPreviewing });
  },
  setIsPreviewingNameOrLogo: (isPreviewingNameOrLogo) => {
    set({ isPreviewingNameOrLogo });
  },
  resetState: () => {
    set({
      organizationalThemeClientData: undefined,
      brandName: null,
      brandSmallLogoUrl: null,
      brandLargeLogoUrl: null,
      brandTheme: null,
      error: null,
      hasLoaded: false,
      viewerIsPreviewing: false,
      isPreviewingNameOrLogo: false,
    });
  },
}));

export const useOrganizationalThemeClientData = (): string | null | undefined =>
  useBrandThemeStore((state) => state.organizationalThemeClientData);
export const useBrandName = (): string | null | undefined => useBrandThemeStore((state) => state.brandName);
export const useBrandSmallLogoUrl = (): string | null | undefined =>
  useBrandThemeStore((state) => state.brandSmallLogoUrl);
export const useBrandLargeLogoUrl = (): string | null | undefined =>
  useBrandThemeStore((state) => state.brandLargeLogoUrl);
export const useBrandTheme = (): ActiveNetworkBranding | null | undefined =>
  useBrandThemeStore((state) => state.brandTheme);
export const useBrandThemeError = (): string | null => useBrandThemeStore((state) => state.error);
export const useHasBrandThemeLoaded = (): boolean => useBrandThemeStore((state) => state.hasLoaded);
export const useViewerIsPreviewing = (): boolean => useBrandThemeStore((state) => state.viewerIsPreviewing);

export const useSetBrandName = (): BrandThemeState['setBrandName'] => useBrandThemeStore((state) => state.setBrandName);
export const useSetOrganizationalThemeClientData = (): BrandThemeState['setOrganizationalThemeClientData'] =>
  useBrandThemeStore((state) => state.setOrganizationalThemeClientData);
export const useSetBrandLogoUrl = (): BrandThemeState['setBrandLogoUrl'] =>
  useBrandThemeStore((state) => state.setBrandLogoUrl);
export const useSetBrandTheme = (): BrandThemeState['setBrandTheme'] =>
  useBrandThemeStore((state) => state.setBrandTheme);
export const useSetBrandThemeError = (): BrandThemeState['setError'] => useBrandThemeStore((state) => state.setError);
export const useSetViewerIsPreviewing = (): BrandThemeState['setViewerIsPreviewing'] =>
  useBrandThemeStore((state) => state.setViewerIsPreviewing);
export const useIsPreviewingNameOrLogo = (): boolean => useBrandThemeStore((state) => state.isPreviewingNameOrLogo);
export const useSetIsPreviewingNameOrLogo = (): BrandThemeState['setIsPreviewingNameOrLogo'] =>
  useBrandThemeStore((state) => state.setIsPreviewingNameOrLogo);
